import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import {
  Button,
  Col,
  ControlLabel,
  FormGroup,
  Grid,
  Row,
} from 'react-bootstrap';
import Empty from '../../components/Empty.jsx';
import OrganizationHeader from '../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import PermissionsForm from '../../components/organizations/Users/Permissions/PermissionsForm.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import { mapFinalFormErrors } from '../../lib/utils.js';
import { selectCurrentOrganizationId } from '../../modules/auth.js';
import { updateNotificationState } from '../../modules/notifications.js';
import {
  retrieveProjects,
  selectReadyProjects,
  selectRetrieveProjectsState,
} from '../../modules/projects.js';
import {
  createRole,
  deleteRole,
  retrieveRole,
  retrieveRoles,
  searchRoles,
  selectRole,
  updateRole,
} from '../../modules/roles.js';

import { retrieveUser, selectUser } from '../../modules/users.js';

import SubHeader from '../../components/SubHeader.jsx';
import {
  retrieveOrgGroups,
  selectFlattenedOrgGroups,
} from '../../modules/orgGroups.js';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state, ownProps) => ({
  role: selectRole(ownProps.projectId)(state),
  projects: selectReadyProjects(state),
  retrieveProjectsSuccess: selectRetrieveProjectsState(state).success,
  user: selectUser(ownProps.userId)(state),
  projectId: ownProps.projectId,
  organizationId: selectCurrentOrganizationId(state),
  editing: !!ownProps.projectId,
  flattenedGroups: selectFlattenedOrgGroups(state),
});

const mapDispatchToProps = {
  createRole,
  updateRole,
  retrieveProjects,
  retrieveRole,
  retrieveRoles,
  deleteRole,
  searchRoles,
  retrieveUser,
  updateNotificationState,
  retrieveOrgGroups,
};

const PermissionsFormContainer = (props) => {
  const {
    userId,
    user,
    retrieveOrgGroups,
    role,
    createRole,
    updateRole,
    editing,
    projects,
    projectId,
    organizationId,
    retrieveProjects,
    retrieveRole,
    retrieveRoles,
    deleteRole,
    retrieveUser,
    searchRoles,
    retrieveProjectsSuccess,
    updateNotificationState,
  } = props;

  const [selectedGroups, updateSelectedGroups] = useState([]);
  const [selectedProjectId, updateSelectedProjectId] = useState(null);
  const [projectsWithRolesIds, setProjectsWithRolesIds] = useState([]);
  const redirectToPermissions = () =>
    pushRoute('organization/users/permissions/show', { userId });
  const projectOptions = () => {
    if (!editing) {
      const projectsWithoutRoles = projects.filter(
        (project) => !projectsWithRolesIds.includes(project.id)
      );

      return projectsWithoutRoles.filter((project) => project.permissionsPage);
    }
    return projects;
  };

  const onGroupSelect = (group) => {
    if (selectedGroups.some((g) => g.id === group.id)) {
      if (!window.confirm('Are you sure you want to detach this group?'))
        return;
      updateSelectedGroups(selectedGroups.filter((g) => g.id !== group.id));
    } else {
      updateSelectedGroups([...selectedGroups, { ...group, roles: [] }]);
    }
  };

  const notify = () => {
    if (user.status === 'no_roles') {
      updateNotificationState({
        isShown: true,
        type: 'success',
        forPath: '/organization/users/permissions/show',
        message:
          'Permissions successfully added. The user has received an invitation to join' +
          ' ChangeSync. User will remain in "Invited" status until they log in.',
      });
    }
  };

  const onDelete = () => {
    if (window.confirm('Are you sure you want to delete these permissions?')) {
      deleteRole(userId, projectId).then(() => {
        searchRoles(userId); // retrieve roles (prevent after deleting role entity)
        redirectToPermissions();
      });
    }
  };

  const onSubmit = (values) => {
    const { roleType, groups, roles } = values;

    const mergedRoles = () =>
      selectedGroups.map((group) => ({
        ...group,
        roles: groups[group.id],
      }));

    const resource = {
      id: selectedProjectId,
      roles: roleType === 'project' ? roles : null,
      groups: roleType === 'group' ? mergedRoles() : null,
    };

    const submitAction = () => {
      const submitPromise = editing
        ? updateRole(userId, projectId, resource)
        : createRole(userId, resource);

      return submitPromise
        .then(() => {
          notify();
          redirectToPermissions();
        })
        .catch(mapErrors);
    };

    if (roleType === 'project') {
      const singleRole = roles.length === 1;
      if (
        (singleRole && ['permissions manager', 'inviter'].includes(roles[0])) ||
        (roles.length === 2 &&
          roles.includes('permissions manager') &&
          roles.includes('inviter'))
      ) {
        if (
          window.confirm(
            'Attention! You assigned an additional role to the current user.' +
              ' We recommend combining this role with Project Leader role.'
          )
        ) {
          submitAction();
        }
      } else {
        submitAction();
      }
    } else submitAction();
  };

  const onDeleteGroup = (id) => {
    updateSelectedGroups(selectedGroups.filter((group) => group.id !== id));
  };

  const validate = (values) => {
    const { roleType, groups, roles } = values;

    const errors = {};

    if (!roleType) {
      errors.roleType = 'Select Role Type';
    }

    if (roleType === 'project' && (!roles || !roles.length)) {
      errors.roles = 'Required Field';
    }

    if (roleType === 'group' && !selectedGroups.length) {
      errors.groups = 'Required Field';
    }

    if (values.roleType === 'group' && selectedGroups.length) {
      selectedGroups.map((group) => {
        if (!groups || !groups[group.id] || !groups[group.id].length) {
          errors[`groups[${group.id}]`] = 'Required Field';
        }
      });
    }

    return errors;
  };

  useEffect(() => {
    retrieveProjects();
    retrieveOrgGroups({ active: true });
    projectId && updateSelectedProjectId(projectId);
    !user && retrieveUser(userId);

    if (!editing) {
      retrieveRoles(userId).then(({ result }) => {
        setProjectsWithRolesIds(result);
      });
    }
  }, [organizationId]);

  useEffect(() => {
    if (editing) {
      selectedProjectId && retrieveRole(userId, selectedProjectId);
    }
  }, [selectedProjectId, organizationId]);

  useEffect(() => {
    if (editing && role) {
      !selectedGroups.length && updateSelectedGroups(role.groups);
    }
  }, [role, organizationId]);

  return (
    <React.Fragment>
      {user && (
        <OrganizationHeader
          userId={userId}
          userName={user.fullName}
          employeeId={user.employeeId}
        />
      )}
      <SubHeader flexJustify small>
        <h2>{editing ? 'Edit Permissions' : 'Add Permissions'}</h2>
        {editing ? (
          <Button className="btn btn-danger" onClick={onDelete}>
            Delete
          </Button>
        ) : null}
      </SubHeader>

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  {projects && projectOptions().length ? (
                    <Row className="m-b-60">
                      <Col xs={12} className="m-b-24">
                        <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
                          <h3>{getOrgTermsContent('project')}</h3>
                        </div>
                        <p className="communication-help text-muted">
                          Select a {getOrgTermsContent('project')} and assign
                          necessary {getOrgTermsContent('project')} or Group
                          role(s).
                        </p>
                      </Col>

                      <Col xs={10}>
                        <FormGroup className="m-b-8">
                          <ControlLabel>
                            Select {getOrgTermsContent('project')}
                          </ControlLabel>
                          <select
                            className="form-control"
                            value={selectedProjectId}
                            onChange={(event) =>
                              updateSelectedProjectId(event.target.value)
                            }
                            disabled={editing}
                          >
                            <option value="" disabled selected>
                              - Select {getOrgTermsContent('project')} -
                            </option>
                            {projectOptions()
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((project) => (
                                <option key={project.id} value={project.id}>
                                  {project.name || ''}
                                </option>
                              ))}
                          </select>
                        </FormGroup>
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col xs={12}>
                        <Empty
                          simplified
                          content={{
                            title:
                              retrieveProjectsSuccess &&
                              getOrgTermsContent('noProjects'),
                          }}
                        />
                      </Col>
                    </Row>
                  )}

                  <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    selectedGroups={selectedGroups}
                    onGroupSelect={onGroupSelect}
                    onDeleteGroup={onDeleteGroup}
                    projectId={selectedProjectId}
                    role={role}
                    userId={userId}
                    component={PermissionsForm}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PermissionsFormContainer);
